import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { PROPERTIES, ZONES, PROPERTY_TYPES } from "@/data/dholera";
import { inr } from "@/lib/format";
import PropertyCard from "@/components/PropertyCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

const MAX_PRICE = 450000000;

export default function Discovery({ onLead, onCalc, onOpenDetail, compareIds, onToggleCompare }) {
  const [zone, setZone] = useState("all");
  const [type, setType] = useState("all");
  const [range, setRange] = useState([0, MAX_PRICE]);

  const filtered = useMemo(
    () =>
      PROPERTIES.filter(
        (p) =>
          (zone === "all" || p.zone === zone) &&
          (type === "all" || p.type === type) &&
          p.plotSqYd * p.pricePerSqYd >= range[0] &&
          p.plotSqYd * p.pricePerSqYd <= range[1]
      ).sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0)),
    [zone, type, range]
  );

  const reset = () => {
    setZone("all");
    setType("all");
    setRange([0, MAX_PRICE]);
  };

  return (
    <section id="parcels" className="relative" data-testid="discovery-section">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent"
        >
          Sec. 02 — Parcel Index
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
          className="mt-5 font-display text-3xl font-medium uppercase tracking-tight md:text-5xl"
        >
          Available parcels
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 grid grid-cols-1 gap-px border border-border bg-border md:grid-cols-12"
          data-testid="filter-dashboard"
        >
          <div className="bg-background p-5 md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Zone / TP Scheme
            </p>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger
                className="mt-3 h-11 rounded-none border-border bg-background font-mono text-xs"
                data-testid="filter-zone-select"
              >
                <SelectValue placeholder="All zones" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" data-testid="zone-option-all">
                  All Zones
                </SelectItem>
                {ZONES.map((z) => (
                  <SelectItem
                    key={z.value}
                    value={z.value}
                    data-testid={`zone-option-${z.value.replace(" ", "-").toLowerCase()}`}
                  >
                    {z.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-background p-5 md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Property Type
            </p>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                className="mt-3 h-11 rounded-none border-border bg-background font-mono text-xs"
                data-testid="filter-type-select"
              >
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" data-testid="type-option-all">
                  All Types
                </SelectItem>
                {PROPERTY_TYPES.map((t) => (
                  <SelectItem
                    key={t}
                    value={t}
                    data-testid={`type-option-${t.split(" ")[0].toLowerCase()}`}
                  >
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-background p-5 md:col-span-4">
            <div className="flex items-baseline justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Total Price Band
              </p>
              <p
                className="font-mono text-xs font-bold"
                data-testid="price-range-value"
              >
                {inr(range[0])} — {inr(range[1])}
              </p>
            </div>
            <Slider
              className="mt-5"
              value={range}
              onValueChange={setRange}
              min={0}
              max={MAX_PRICE}
              step={500000}
              data-testid="filter-price-slider"
            />
          </div>

          <button
            onClick={reset}
            className="flex items-center justify-center gap-2 bg-background p-5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground md:col-span-2"
            data-testid="filter-reset-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        </motion.div>

        <div className="mt-10 flex items-baseline justify-between">
          <p
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground"
            data-testid="results-count"
          >
            {String(filtered.length).padStart(2, "0")} parcels in index
          </p>
          <p className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/60 md:block">
            Mock data · illustrative
          </p>
        </div>

        <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <PropertyCard
                key={p.id}
                property={p}
                onLead={onLead}
                onCalc={onCalc}
                onOpenDetail={onOpenDetail}
                inCompare={compareIds.includes(p.id)}
                onToggleCompare={onToggleCompare}
              />
            ))}
          </AnimatePresence>
          {filtered.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-full border border-dashed border-border p-16 text-center"
              data-testid="empty-results"
            >
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                No parcels match — widen the price band
              </p>
              <button
                onClick={reset}
                className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-accent underline-offset-4 hover:underline"
                data-testid="empty-reset-btn"
              >
                Reset filters
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
